import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, onSnapshot } from 'firebase/firestore';
import { auth, db, handleFirestoreError, OperationType } from '../lib/firebase';
import { testGeminiConnection } from '../services/gemini';
import { Organisation, OrganisationMember } from '../types';
import { Diagnostics, GoldenPathStep } from '../lib/diagnostics';

interface AuthContextType {
  user: User | null;
  globalRole: 'learner' | 'tutor' | null;
  role: 'learner' | 'tutor' | null;
  userIntent: string | null;
  orgRole: 'owner' | 'admin' | 'tutor' | 'learner' | null;
  hasTutorAccess: boolean;
  isPlatformAdmin: boolean;
  loading: boolean;
  authError: string | null;
  systemError: string | null;
  isOffline: boolean;
  login: () => void;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  logout: () => Promise<void>;
  toggleRole: () => Promise<void>;
  updateUserRole: (newRole: 'learner' | 'tutor', orgRequested?: boolean, userIntent?: string) => Promise<void>;
  clearAuthError: () => void;
  setAuthError: (error: string | null) => void;
  clearSystemError: () => void;
  performApiTest: () => Promise<{ success: boolean; message: string }>;
  isLoggingIn: boolean;
  isAuthModalOpen: boolean;
  authMode: 'login' | 'register';
  openAuth: (mode: 'login' | 'register', options?: { intent?: string }) => void;
  closeAuth: () => void;
  memberships: OrganisationMember[];
  currentOrg: Organisation | null;
  setCurrentOrg: (org: Organisation | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [globalRole, setGlobalRole] = useState<'learner' | 'tutor' | null>(null);
  const [role, setRole] = useState<'learner' | 'tutor' | null>(null);
  const [userIntent, setUserIntent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [systemError, setSystemError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [memberships, setMemberships] = useState<OrganisationMember[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organisation | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);

  const openAuth = (mode: 'login' | 'register', options?: { intent?: string }) => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
    if (options?.intent) {
      sessionStorage.setItem('lumina_signup_intent', options.intent);
    }
  };

  const closeAuth = () => {
    setIsAuthModalOpen(false);
    setAuthError(null);
  };

  useEffect(() => {
    // Listen for Firestore connectivity issues
    const handleConnectivity = (e: any) => {
      if (e.detail?.isOffline !== undefined) {
        setIsOffline(e.detail.isOffline);
      }
    };
    window.addEventListener('firestoreConnectivity', handleConnectivity);

    let unsubscribeMembers: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (unsubscribeMembers) {
        unsubscribeMembers();
        unsubscribeMembers = null;
      }

      if (currentUser) {
        // Log diagnostic step
        Diagnostics.logStep(GoldenPathStep.LOGIN, 'success', { uid: currentUser.uid, email: currentUser.email });

        // Listen for memberships
        const q = query(collection(db, 'organisation_members'), where('userId', '==', currentUser.uid));
        unsubscribeMembers = onSnapshot(q, async (snapshot) => {
          const members = snapshot.docs.map(d => d.data() as OrganisationMember);
          setMemberships(members);
          
          if (members.length > 0) {
            // Logic to pick a currentOrg
            const savedOrgId = localStorage.getItem('last_org_id');
            const targetOrgId = savedOrgId && members.find(m => m.orgId === savedOrgId) ? savedOrgId : members[0].orgId;
            
            try {
              const orgDoc = await getDoc(doc(db, 'organisations', targetOrgId));
              if (orgDoc.exists()) {
                setCurrentOrg({ id: orgDoc.id, ...orgDoc.data() } as Organisation);
              }
            } catch (error) {
              handleFirestoreError(error, OperationType.GET, `organisations/${targetOrgId}`);
            }
          } else {
            setCurrentOrg(null);
          }
        }, (error) => {
          handleFirestoreError(error, OperationType.LIST, 'organisation_members');
        });

        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();

            if (['suspended', 'disabled', 'deleted'].includes(data.status)) {
                await signOut(auth);
                setAuthError("Your Lumina Learn account is suspended. Please contact support if you believe this is an error.");
                setIsLoggingIn(false);
                setLoading(false);
                return;
            }
            
            let currentRole = data.role;
            let currentGlobalRole = data.globalRole || data.role || null;
            let currentPlatformAdmin = !!data.isPlatformAdmin;
            
            const email = currentUser.email;
            const isPlatformAdminEmail = email === 'info@luminalearn.pro' || email === 'victord70@gmail.com'; 
            
            let targetGlobalRole = currentGlobalRole;
            let targetRole = currentRole;
            if (email === 'victord70@gmail.com') targetGlobalRole = 'tutor';
            if (email === 'info@luminalearn.pro') targetGlobalRole = 'tutor';
            if (email === 'kunle.david@crowninteractive.com') targetGlobalRole = 'tutor';
            if (email === 'kdavid70@outlook.com') targetGlobalRole = 'learner';

            let updates: any = {};
            if (currentGlobalRole !== targetGlobalRole) {
                updates.globalRole = targetGlobalRole;
                currentGlobalRole = targetGlobalRole;
                if (!targetRole) {
                  updates.role = targetGlobalRole;
                  currentRole = targetGlobalRole;
                }
            }
            if (!data.globalRole && currentGlobalRole) {
                updates.globalRole = currentGlobalRole;
            }
            if (isPlatformAdminEmail && !currentPlatformAdmin) {
                updates.isPlatformAdmin = true;
                currentPlatformAdmin = true;
            }

            if (Object.keys(updates).length > 0) {
               setDoc(doc(db, 'users', currentUser.uid), updates, { merge: true }).catch(() => {});
            }

            setGlobalRole(currentGlobalRole);
            setRole(currentRole);
            setUserIntent(data.userIntent || null);
            setIsPlatformAdmin(currentPlatformAdmin);
          } else {
            // Create new user
            const email = currentUser.email;
            const isPlatformAdminEmail = email === 'info@luminalearn.pro' || email === 'victord70@gmail.com';
            
            let newRole: 'tutor' | 'learner' | null = null;
            if (email === 'victord70@gmail.com') newRole = 'tutor';
            if (email === 'info@luminalearn.pro') newRole = 'tutor';
            if (email === 'kunle.david@crowninteractive.com') newRole = 'tutor';
            if (email === 'kdavid70@outlook.com') newRole = 'learner';

            await setDoc(doc(db, 'users', currentUser.uid), {
              uid: currentUser.uid,
              displayName: currentUser.displayName,
              email: currentUser.email,
              photoURL: currentUser.photoURL,
              role: newRole,
              globalRole: newRole,
              isPlatformAdmin: isPlatformAdminEmail,
              createdAt: serverTimestamp(),
            });
            setGlobalRole(newRole);
            setRole(newRole);
            setUserIntent(null);
            setIsPlatformAdmin(isPlatformAdminEmail);
          }
        } catch (error) {
          handleFirestoreError(error, OperationType.GET, `users/${currentUser.uid}`);
        }
      } else {
        setRole(null);
      }
      setLoading(false);
      setIsLoggingIn(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async () => {
    if (isLoggingIn) return;
    
    setIsLoggingIn(true);
    setAuthError(null);
    
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      setIsLoggingIn(false);
      if (error.code === 'auth/popup-blocked') {
        setAuthError('Login popup was blocked by your browser. Please allow popups for this site and try again.');
        Diagnostics.logStep(GoldenPathStep.LOGIN, 'fail', { error: 'Popup blocked' });
      } else if (error.code === 'auth/cancelled-popup-request') {
        console.log('Login popup request cancelled.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setAuthError('Login attempt was cancelled by closing the window.');
      } else {
        setAuthError(`An error occurred during login (${error.code}). Please try again.`);
        Diagnostics.logStep(GoldenPathStep.LOGIN, 'fail', { error: error.code });
        console.error('Login error:', error);
      }
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      setIsLoggingIn(false);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setAuthError('Invalid email or password.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Please enter a valid email address.');
      } else {
        setAuthError(`Login failed: ${error.message}`);
      }
      throw error;
    }
  };

  const registerWithEmail = async (email: string, password: string) => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    setAuthError(null);
    try {
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      // Immediately send verification email
      await sendEmailVerification(userCred.user);
    } catch (error: any) {
      setIsLoggingIn(false);
      if (error.code === 'auth/email-already-in-use') {
        setAuthError('This email is already registered. Please login instead.');
      } else if (error.code === 'auth/weak-password') {
        setAuthError('Password is too weak. Please use at least 6 characters.');
      } else if (error.code === 'auth/operation-not-allowed') {
        setAuthError('Email and password signup is not enabled yet. Please enable Email Password provider in Firebase Authentication.');
      } else {
        setAuthError(`Registration failed: ${error.message}`);
      }
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      try {
        await sendEmailVerification(auth.currentUser);
      } catch (error: any) {
        setAuthError(`Failed to resend verification email: ${error.message}`);
      }
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        setAuthError('No user found with this email address.');
      } else {
        setAuthError(`Password reset failed: ${error.message}`);
      }
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  const toggleRole = async () => {
    if (!user || !role) return;
    const newRole = role === 'learner' ? 'tutor' : 'learner';
    try {
      await setDoc(doc(db, 'users', user.uid), { role: newRole }, { merge: true });
      // Log the audit
      await setDoc(doc(collection(db, 'role_audit_logs')), {
        changedUserId: user.uid,
        changedBy: user.uid,
        oldRole: role,
        newRole: newRole,
        scope: 'global',
        createdAt: serverTimestamp(),
        reason: 'User toggled role'
      });
      setRole(newRole);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
    }
  };

  const updateUserRole = async (newRole: 'learner' | 'tutor', orgRequested?: boolean, userIntent?: string) => {
    if (!user) return;
    try {
      const updates: any = { role: newRole, globalRole: newRole };
      if (orgRequested) {
        updates.orgRequested = true;
      }
      if (userIntent) {
        updates.userIntent = userIntent;
      }
      await setDoc(doc(db, 'users', user.uid), updates, { merge: true });
      // Log the audit
      if (globalRole !== newRole) {
        await setDoc(doc(collection(db, 'role_audit_logs')), {
          changedUserId: user.uid,
          changedBy: user.uid,
          oldRole: globalRole || 'none',
          newRole: newRole,
          scope: 'global',
          createdAt: serverTimestamp(),
          reason: 'Initial role selection',
          userIntent: userIntent || 'none'
        });
      }
      setGlobalRole(newRole);
      setRole(newRole);
      if (userIntent) setUserIntent(userIntent);
      sessionStorage.removeItem('lumina_signup_intent');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
    }
  };

  const clearAuthError = () => setAuthError(null);
  const clearSystemError = () => setSystemError(null);
  const performApiTest = async () => {
    return await testGeminiConnection();
  };

  const handleSetCurrentOrg = (org: Organisation | null) => {
    setCurrentOrg(org);
    if (org) {
      localStorage.setItem('last_org_id', org.id);
    } else {
      localStorage.removeItem('last_org_id');
    }
  };

  const orgRole = currentOrg ? memberships.find(m => m.orgId === currentOrg.id)?.role || null : null;
  const isGlobalTutor = globalRole === 'tutor' || userIntent === 'professional';
  const isOrgAdminOrTutor = orgRole === 'admin' || orgRole === 'tutor' || orgRole === 'owner';
  const hasTutorAccess = isGlobalTutor || isOrgAdminOrTutor;

  useEffect(() => {
    if (!loading && user && globalRole === 'learner' && orgRole === 'learner') {
      console.log('User organisation role resolved: learner. No tutor capabilities.');
    }
  }, [loading, user, orgRole, globalRole]);

  // Force learner role back to student if they are somehow stuck in tutor mode UI
  useEffect(() => {
    if (!loading && user && !hasTutorAccess && role === 'tutor') {
      setDoc(doc(db, 'users', user.uid), { role: 'learner' }, { merge: true }).catch(console.error);
    }
  }, [hasTutorAccess, role, loading, user]);

  return (
    <AuthContext.Provider value={{ 
      user, globalRole, role, userIntent, orgRole, hasTutorAccess, isPlatformAdmin, loading, authError, systemError, isOffline, 
      login, loginWithEmail, registerWithEmail, resetPassword, resendVerificationEmail,
      logout, toggleRole, updateUserRole, clearAuthError, setAuthError, clearSystemError, 
      performApiTest, isLoggingIn, isAuthModalOpen, authMode, 
      openAuth, closeAuth, memberships, currentOrg, 
      setCurrentOrg: handleSetCurrentOrg 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
